import { IconButton } from "@material-ui/core";
import { styled, type Theme } from "@material-ui/core/styles";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { useIntl } from "react-intl";

export type BellProps = {
  unreadCount: number;
  actionRequiredCount: number;
  onClick: () => void;
};

const BellButton = styled(IconButton)(({ theme }) => ({
  position: "relative",
  width: 40,
  height: 40,
  color: theme.palette.common.white,
  padding: theme.spacing(1),
}));

const BellIcon = styled(NotificationsIcon)<Theme, { $quiet: boolean }>(
  ({ $quiet }) => ({
    opacity: $quiet ? 0.5 : 0.9,
    fontSize: 22,
  }),
);

const Badge = styled("span")(({ theme }) => ({
  position: "absolute",
  top: 4,
  right: 4,
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 10,
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontSize: 9,
  fontWeight: 700,
  lineHeight: "16px",
  textAlign: "center",
  border: `1.5px solid ${theme.palette.brand.main}`,
}));

const Dot = styled("span")(({ theme }) => ({
  position: "absolute",
  top: 7,
  right: 7,
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.error.main,
  border: `1.5px solid ${theme.palette.brand.main}`,
}));

export const Bell: FC<BellProps> = ({
  unreadCount,
  actionRequiredCount,
  onClick,
}) => {
  const { formatMessage } = useIntl();

  const ariaLabel = formatMessage(
    {
      id: "notifications.bell.ariaLabel",
      defaultMessage:
        "{count, plural, =0 {No new notifications} one {# new notification} other {# new notifications}}",
    },
    { count: unreadCount },
  );

  const showBadge = actionRequiredCount > 0;
  const showDot = !showBadge && unreadCount > 0;
  const isQuiet = unreadCount === 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    onClick();
  };

  return (
    <BellButton
      data-testid="notifications-bell"
      aria-label={ariaLabel}
      onClick={handleClick}
    >
      <BellIcon $quiet={isQuiet} />
      {showBadge && (
        <Badge data-testid="notifications-bell-badge">
          {actionRequiredCount > 9 ? "9+" : actionRequiredCount}
        </Badge>
      )}
      {showDot && <Dot data-testid="notifications-bell-dot" />}
    </BellButton>
  );
};
